import React, { Component } from 'react';
import { components } from '@kurtosys/ksys-app-components';
import { common } from '@kurtosys/ksys-app-template';
import { computed, makeObservable } from 'mobx';
import { inject, observer } from 'mobx-react';

import { InjectedStyledComponent } from '../shared/InjectedStyledComponent.jsx';

import DatePickerComponent from './styledComponents/DatePickerComponent.jsx';
import Label from './styledComponents/Label.jsx';
import Wrapper from './styledComponents/Wrapper.jsx';
import { IDatePickerProps, IDatePickerState } from './models';

type IBaseDatePickerProps = components.base.DatePicker.models.IDatePickerProps;

const {
	DaysOfWeek,
	printISODate,
	printISODateFromString,
} = common.commonUtils;
const DatePickerDefaultLocalization = components.base.DatePicker.models.DatePickerDefaultLocalization;
const isoAdapter = components.base.DatePicker.DateAdapter;
class RawDatePicker extends Component<IDatePickerProps, IDatePickerState> {
	static configurationKey = 'datePicker' as const;
	static styleKey = 'datePicker' as const;

	constructor(props: IDatePickerProps) {
		super(props);
		makeObservable(this);
	}

	@computed
	get labelProps() {
		const { datePickerStore } = this.props;
		const labelProps = datePickerStore.componentProps.label || {};
		labelProps.value = datePickerStore.labelValue;
		return labelProps;
	}

	@computed
	get datePickerComponentProps(): IBaseDatePickerProps {
		const { datePickerStore, appStore } = this.props;
		const { value, componentProps, options, allowedValues, setValue } = datePickerStore;
		const { label, ...datePickerComponentProps } = componentProps;
		const optionsLabel = options.label;
		const inputCulture = (appStore && appStore.getInput('culture')) || 'en-GB';
		const localization = {
			...DatePickerDefaultLocalization,
			...datePickerComponentProps.localization,
			locale: inputCulture,
		};
		const props: IBaseDatePickerProps = {
			localization,
			name: datePickerComponentProps.name || optionsLabel || '',
			identifier: datePickerComponentProps.identifier || '',
			direction: datePickerComponentProps.direction || 'right',
			required: datePickerComponentProps.required || false,
			firstDayOfWeek: datePickerComponentProps.firstDayOfWeek || DaysOfWeek.Sunday,
			disabled: allowedValues ? allowedValues.length === 0 : false,
			min: printISODateFromString(datePickerStore.minAllowedValue),
			max: printISODateFromString(datePickerStore.maxAllowedValue),
			dateAdapter: {
				...isoAdapter,
				format: (date: Date) => {
					return datePickerStore.formatValue(printISODate(date));
				},
			},
			isDateDisabled: (date: Date) => {
				return allowedValues ? !allowedValues.includes(printISODate(date)) : false;
			},
			value: printISODateFromString(value),
			onChange: setValue,
		};
		return props;
	}

	render() {
		const { className, datePickerStore } = this.props;

		if (!datePickerStore || !datePickerStore.show) {
			return null;
		}

		return (
			<Wrapper className={className} key={datePickerStore.inputKey}>
				<Label {...this.labelProps} />
				<DatePickerComponent {...this.datePickerComponentProps} />
			</Wrapper>
		);
	}
}

export const DatePicker = inject('appStore')(observer(RawDatePicker));
export default InjectedStyledComponent(DatePicker, 'datePicker');
